/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { bindActionCreators } from "redux";
import { authActionCreators } from "../../service/action-creators";
import Loader from "../Loader";
import "./VerifyEmailOtp.css";

const OTP_DURATION = 5 * 60 // 300 seconds

const getNextCooldownSeconds = (countAfterResend) => {
  if (countAfterResend < 3) return 0
  if (countAfterResend === 3) return 20 * 60   // 20 min
  if (countAfterResend === 4) return 30 * 60   // 30 min
  return (30 + (countAfterResend - 4) * 15) * 60 // 45, 60, 75 min…
}

const formatTime = (secs) => {
  const m = String(Math.floor(secs / 60)).padStart(2, '0')
  const s = String(secs % 60).padStart(2, '0')
  return `${m}:${s}`
}

const timerColor = (secs) => {
  if (secs > 300) return '#00a566'
  if (secs > 60) return '#f59e0b'
  return '#ef4444'
}

function ResendEmailVerificationLink() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { ResetResponse, ResendEmailTokenToUser, VerifyEmailAddress } =
    bindActionCreators(authActionCreators, dispatch);

  const auth = useSelector((state) => state?.auth);
  const isLoading = auth?.isLoading;
  const email = auth?.user?.email || "";
  const lsKey = email ? `otp_state_${email}` : null;

  const [digits, setDigits] = useState(["", "", "", ""]);
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const [isResending, setIsResending] = useState(false);

  // Timer state
  const [codeTimeLeft, setCodeTimeLeft] = useState(OTP_DURATION);
  const [resendCount, setResendCount] = useState(0);
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const cooldownUntilRef = useRef(0);

  // Load persisted state (survives page refresh)
  useEffect(() => {
    if (!lsKey) return;
    try {
      const saved = JSON.parse(localStorage.getItem(lsKey) || '{}');
      if (saved.resendCount != null) setResendCount(saved.resendCount);
      if (saved.cooldownUntil) {
        cooldownUntilRef.current = saved.cooldownUntil;
        const rem = Math.max(0, Math.ceil((saved.cooldownUntil - Date.now()) / 1000));
        setCooldownLeft(rem);
      }
    } catch { }
  }, [email]);

  // Tick every second
  useEffect(() => {
    const t = setInterval(() => {
      setCodeTimeLeft(prev => Math.max(0, prev - 1));
      if (cooldownUntilRef.current > 0) {
        const rem = Math.max(0, Math.ceil((cooldownUntilRef.current - Date.now()) / 1000));
        setCooldownLeft(rem);
        if (rem === 0) cooldownUntilRef.current = 0;
      }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  // Digit handlers
  const handleDigitChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...digits];
    updated[index] = value;
    setDigits(updated);
    if (value && index < 3) inputRefs[index + 1].current?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0)
      inputRefs[index - 1].current?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
    const updated = ["", "", "", ""];
    pasted.split("").forEach((ch, i) => { updated[i] = ch; });
    setDigits(updated);
    const nextEmpty = updated.findIndex((d) => d === "");
    inputRefs[nextEmpty === -1 ? 3 : nextEmpty].current?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const code = digits.join("");
    if (code.length < 4) {
      toast.error("Please enter the complete 4-digit code");
      return;
    }
    setIsResending(false);
    VerifyEmailAddress(email, code);
  };

  const handleResend = () => {
    const newCount = resendCount + 1;
    const cooldownSecs = getNextCooldownSeconds(newCount);
    const cooldownEnd = cooldownSecs > 0 ? Date.now() + cooldownSecs * 1000 : 0;

    setResendCount(newCount);
    cooldownUntilRef.current = cooldownEnd;
    setCooldownLeft(cooldownSecs);
    setCodeTimeLeft(OTP_DURATION); // restart 15-min window

    if (lsKey) {
      localStorage.setItem(lsKey, JSON.stringify({ resendCount: newCount, cooldownUntil: cooldownEnd }));
    }

    setIsResending(true);
    setDigits(["", "", "", ""]);
    ResendEmailTokenToUser(email);
    setTimeout(() => inputRefs[0].current?.focus(), 100);
  };

  useEffect(() => {
    if (auth?.response?.state === "SUCCESS") {
      if (isResending) {
        toast.success("A new code has been sent to your email.");
        ResetResponse();
        setIsResending(false);
      } else {
        if (lsKey) localStorage.removeItem(lsKey);
        toast.success(auth?.response?.message ?? "Email verified!");
        setTimeout(() => {
          ResetResponse();
          history.push("/account/email-verify-success");
        }, 1500);
      }
    } else if (auth?.response?.state === "ERROR") {
      toast.error(auth?.response?.message ?? "Something went wrong");
      setTimeout(() => ResetResponse(), 1500);
    }
  }, [auth?.response?.state]);

  const otpCode = digits.join("");
  const freeResendsLeft = Math.max(0, 3 - resendCount);

  return (
    <>
      {isLoading && <Loader />}
      <ToastContainer autoClose={3000} />

      <div className="verify-otp-page">
        <div className="verify-bg-layer" style={{ backgroundImage: "url(/images/dev/background.jpg)" }} />
        <div className="verify-dark-overlay" />
        <div className="verify-otp-card">

          <div className="verify-otp-icon">
            <svg width="38" height="38" viewBox="0 0 24 24" fill="none"
              stroke="#00a566" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>

          <h2>Verify your email</h2>
          <p className="subtitle">
            Enter the 4-digit code sent to<br />
            <strong>{email || "your email address"}</strong>
          </p>

          <form onSubmit={handleSubmit}>
            <div className="digit-row" onPaste={handlePaste}>
              {digits.map((d, i) => (
                <input
                  key={i}
                  ref={inputRefs[i]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={d}
                  onChange={(e) => handleDigitChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  className={`digit-box${d ? " filled" : ""}`}
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={isLoading || otpCode.length < 4}
              style={{
                background: (isLoading || otpCode.length < 4) ? '#d1d5db' : '#00a566',
                color: (isLoading || otpCode.length < 4) ? '#6b7280' : '#ffffff',
                cursor: (isLoading || otpCode.length < 4) ? 'not-allowed' : 'pointer',
              }}
              className="w-full py-3 mt-4 rounded-full text-base font-semibold border-none block transition-colors duration-200"
            >
              {isLoading && !isResending ? "Verifying..." : "Verify Email"}
            </button>
          </form>

          <hr className="verify-divider" />

          {/* ── Timer / Resend area ── */}
          {codeTimeLeft > 0 ? (
            <p className="otp-timer" style={{ color: timerColor(codeTimeLeft) }}>
              Code expires in <strong>{formatTime(codeTimeLeft)}</strong>
            </p>
          ) : cooldownLeft > 0 ? (
            <div className="otp-cooldown">
              <p>Too many requests. Try again in</p>
              <strong className="otp-cooldown-timer">{formatTime(cooldownLeft)}</strong>
            </div>
          ) : (
            <div className="resend-area">
              <p className="verify-hint">Code expired. Request a new one.</p>
              <button
                onClick={handleResend}
                disabled={isLoading}
                className="resend-btn"
              >
                {isLoading && isResending ? "Sending..." : "Resend Code"}
              </button>
              {freeResendsLeft > 0 && (
                <p className="resend-remaining">
                  {freeResendsLeft} free resend{freeResendsLeft !== 1 ? "s" : ""} remaining
                </p>
              )}
            </div>
          )}

        </div>
      </div>
    </>
  );
}

export default ResendEmailVerificationLink;
